import {
  assigned_risk_questions,
  fetch_assigned_questions_to_assessment,
  mapRiskBindingsToAssessments,
  matching_risk_choice,
  normalize_label,
  split_answer_values,
  submit_risk_assessment_answers,
  to_number,
} from './request_portal_helpers';

function submitted_answer_maps(submitted_answers = []) {
  const maps = { by_question_id: new Map(), by_question_text: new Map() };

  submitted_answers.forEach((answer) => {
    const question_id = to_number(answer?.question_id);
    const question_text = normalize_label(answer?.question_text);

    if (question_id) maps.by_question_id.set(question_id, answer);
    if (question_text) maps.by_question_text.set(question_text, answer);
  });

  return maps;
}

function source_answer_for_risk_question(risk_question = {}, maps = {}) {
  const risk_question_id = to_number(risk_question?.id);
  return maps.by_question_id.get(risk_question_id)
    || maps.by_question_text.get(normalize_label(risk_question?.text || risk_question?.question_text));
}

function matched_risk_values(risk_question = {}, source_answer = {}) {
  return split_answer_values(source_answer?.answer_text)
    .map((value) => matching_risk_choice(value, risk_question?.choices))
    .filter(Boolean)
    .map((choice) => choice?.option || choice?.choice || choice)
    .filter(Boolean);
}

function risk_answer_entry(risk_question = {}, maps = {}) {
  const risk_question_id = to_number(risk_question?.id);
  const source_answer = source_answer_for_risk_question(risk_question, maps);
  const matched_values = matched_risk_values(risk_question, source_answer);

  if (!risk_question_id || !matched_values.length) return null;

  return [
    String(risk_question_id),
    matched_values.length === 1 ? matched_values[0] : matched_values,
  ];
}

async function assessment_with_questions(assessment = {}) {
  try {
    const questions_payload = await fetch_assigned_questions_to_assessment(assessment.id);
    return { ...assessment, questions: assigned_risk_questions(questions_payload) };
  } catch {
    return { ...assessment, questions: [] };
  }
}

function risk_submission_payload(assessment = {}, answers = {}) {
  return {
    assessment_id: to_number(assessment?.id),
    answers,
  };
}

function has_risk_answers(item = {}) {
  return item.assessment_id && Object.keys(item.answers).length;
}

async function submit_risk_payload(entity_id, item = {}) {
  return submit_risk_assessment_answers({
    entity_id,
    assessment_id: item.assessment_id,
    answers: item.answers,
  });
}

export default {
  risk_answers_for_assessment(assessment = {}, submitted_answers = []) {
    const maps = submitted_answer_maps(submitted_answers);
    return Object.fromEntries(
      (Array.isArray(assessment?.questions) ? assessment.questions : [])
        .map((risk_question) => risk_answer_entry(risk_question, maps))
        .filter(Boolean)
    );
  },
  async submit_case_risk_answers({ entity_profile_id, submitted_answers, entity_type_id = null }) {
    const entity_id = to_number(entity_profile_id);
    const resolved_entity_type_id = to_number(entity_type_id) || to_number(this.selected_entity_type_id);
    if (!entity_id || !resolved_entity_type_id) return [];

    let config_data = {};
    try {
      config_data = await this.ensure_case_type_config(this.selected_case_type || {
        id: resolved_entity_type_id,
      });
    } catch {
      return [];
    }

    const assessments = await Promise.all(
      mapRiskBindingsToAssessments(config_data).map(assessment_with_questions)
    );
    const submissions = assessments
      .map((assessment) => risk_submission_payload(
        assessment,
        this.risk_answers_for_assessment(assessment, submitted_answers)
      ))
      .filter(has_risk_answers);
    const results = await Promise.allSettled(
      submissions.map((item) => submit_risk_payload(entity_id, item))
    );
    const failed_submission = results.find((result) => result.status === 'rejected');

    if (failed_submission) throw failed_submission.reason;

    return results.map((result) => result.value);
  },
};
